<template>
  <section class="hero" aria-labelledby="hero-title">
    <div class="hero__bg" aria-hidden="true">
      <span class="hero__ghost">AI × BACKEND</span>
    </div>

    <div class="container hero__grid">
      <div class="hero__body">
        <p class="eyebrow hero__eyebrow" v-reveal>
          AI-Driven Development &times; Backend Engineering
        </p>

        <h1 id="hero-title" class="hero__title" v-reveal="90">
          AIの<span class="hl-accent">速さと精度</span>に、<br />経験の<span class="hl-underline">視点と発想</span>で。
        </h1>

        <p class="hero__lead" v-reveal="180">
          Webサービスのバックエンド開発で培った技術に、生成AIとAI駆動開発を掛け合わせる。速く形にして、品質は人とテストで担保する。栃木・宇都宮から、確かなものづくりを。
        </p>

        <div class="hero__cta" v-reveal="260">
          <a href="#contact" class="btn btn--primary" @click="scrollTo($event, 'contact')">
            まずは相談する
            <svg class="btn__arrow" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M2 8h11M9 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </a>
          <a href="#service" class="btn btn--ghost" @click="scrollTo($event, 'service')">
            サービスを見る
          </a>
        </div>

        <ul class="hero__meta" v-reveal="340">
          <li><span>要件定義</span></li>
          <li aria-hidden="true">→</li>
          <li><span>開発</span></li>
          <li aria-hidden="true">→</li>
          <li><span>運用保守</span></li>
          <li class="hero__meta-tag">一貫対応</li>
        </ul>

        <!-- Mobile key visual (shown only <=900px, below the hero copy) -->
        <div class="kv-mobile" v-reveal="200" aria-hidden="true">
          <svg class="kv-svg" viewBox="0 0 340 120" xmlns="http://www.w3.org/2000/svg" font-family="'IBM Plex Mono',ui-monospace,Menlo,monospace">
            <g id="m-dial" transform="translate(60 60)">
              <circle r="44" fill="none" stroke="rgba(20,22,27,0.14)" stroke-width="1.2"></circle>
              <circle r="30" fill="none" stroke="rgba(20,22,27,0.14)" stroke-width="1"></circle>
              <g stroke="rgba(20,22,27,0.4)" stroke-width="1">
                <line x1="0" y1="-44" x2="0" y2="-39"></line><line x1="44" y1="0" x2="39" y2="0"></line><line x1="0" y1="44" x2="0" y2="39"></line><line x1="-44" y1="0" x2="-39" y2="0"></line>
              </g>
              <circle r="3.5" fill="#53301b"></circle>
            </g>
            <g id="m-sweep">
              <defs>
                <linearGradient id="kvm-grad" x1="0" y1="1" x2="1" y2="0">
                  <stop offset="0%" stop-color="#857243" stop-opacity="0"></stop>
                  <stop offset="100%" stop-color="#53301b" stop-opacity="1"></stop>
                </linearGradient>
              </defs>
              <path d="M 60 30 A 30 30 0 0 1 86 75" fill="none" stroke="url(#kvm-grad)" stroke-width="3" stroke-linecap="round"></path>
            </g>
            <g font-size="9" letter-spacing="1.5">
              <circle cx="130" cy="34" r="3" fill="#53301b"></circle><text x="142" y="38" fill="#14161b">01 GENERATE</text>
              <circle cx="130" cy="60" r="3" fill="none" stroke="#14161b" stroke-width="1.2"></circle><text x="142" y="64" fill="#14161b">02 REVIEW</text>
              <circle cx="130" cy="86" r="3" fill="none" stroke="#14161b" stroke-width="1.2"></circle><text x="142" y="90" fill="#14161b">03 VERIFY</text>
              <path d="M228 82 l4 4 8 -9" fill="none" stroke="#53301b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
            </g>
          </svg>
        </div>
      </div>

      <div class="hero__visual" v-reveal="220" aria-hidden="true">
        <div class="kv-desktop">
          <svg class="kv-svg" viewBox="0 0 440 540" xmlns="http://www.w3.org/2000/svg" font-family="'IBM Plex Mono',ui-monospace,Menlo,monospace">
            <g id="loop-dial" transform="translate(220 250)">
              <circle r="170" fill="none" stroke="rgba(20,22,27,0.14)" stroke-width="1.4"></circle>
              <circle r="140" fill="none" stroke="rgba(20,22,27,0.14)" stroke-width="1"></circle>
              <circle r="86" fill="none" stroke="rgba(20,22,27,0.14)" stroke-width="1"></circle>
              <g id="dial-ticks" stroke="rgba(20,22,27,0.4)" stroke-width="1">
                <line x1="0" y1="-170" x2="0" y2="-160"></line><line x1="85" y1="-147.2" x2="80" y2="-138.6"></line><line x1="147.2" y1="-85" x2="138.6" y2="-80"></line><line x1="170" y1="0" x2="160" y2="0"></line><line x1="147.2" y1="85" x2="138.6" y2="80"></line><line x1="85" y1="147.2" x2="80" y2="138.6"></line><line x1="0" y1="170" x2="0" y2="160"></line><line x1="-85" y1="147.2" x2="-80" y2="138.6"></line><line x1="-147.2" y1="85" x2="-138.6" y2="80"></line><line x1="-170" y1="0" x2="-160" y2="0"></line><line x1="-147.2" y1="-85" x2="-138.6" y2="-80"></line><line x1="-85" y1="-147.2" x2="-80" y2="-138.6"></line>
              </g>
            </g>
            <g id="sweep">
              <defs>
                <linearGradient id="sweep-grad" x1="0" y1="1" x2="1" y2="0">
                  <stop offset="0%" stop-color="#857243" stop-opacity="0"></stop>
                  <stop offset="100%" stop-color="#53301b" stop-opacity="1"></stop>
                </linearGradient>
              </defs>
              <path id="sweep-arc" d="M 220 110 A 140 140 0 0 1 341.2 320" fill="none" stroke="url(#sweep-grad)" stroke-width="4" stroke-linecap="round"></path>
            </g>
            <g id="center-mark" transform="translate(220 250)">
              <circle r="6" fill="#53301b"></circle>
              <line x1="0" y1="-20" x2="0" y2="-38" stroke="#14161b" stroke-width="1.4"></line>
              <line x1="0" y1="20" x2="0" y2="38" stroke="#14161b" stroke-width="1.4"></line>
              <line x1="-20" y1="0" x2="-38" y2="0" stroke="#14161b" stroke-width="1.4"></line>
              <line x1="20" y1="0" x2="38" y2="0" stroke="#14161b" stroke-width="1.4"></line>
              <text x="0" y="62" text-anchor="middle" font-size="9" letter-spacing="2" fill="#6c707a">SHIP · VERIFIED</text>
            </g>
            <g id="station-pings" fill="#857243">
              <circle id="ping-generate" class="kv-ping" cx="220" cy="110" r="7"></circle>
              <circle id="ping-review" class="kv-ping" cx="319" cy="349" r="7"></circle>
              <circle id="ping-verify" class="kv-ping" cx="121" cy="349" r="7"></circle>
            </g>
            <g id="station-generate">
              <rect x="150" y="52" width="140" height="76" rx="5" fill="#ffffff" stroke="#53301b" stroke-width="1.4"></rect>
              <text x="164" y="72" font-size="9" letter-spacing="1.5" fill="#40230f">01 GENERATE</text>
              <rect x="164" y="82" width="80" height="6" rx="2" fill="rgba(83,48,27,0.09)" stroke="rgba(83,48,27,0.28)" stroke-width="0.8"></rect>
              <rect x="164" y="94" width="100" height="6" rx="2" fill="rgba(83,48,27,0.09)" stroke="rgba(83,48,27,0.28)" stroke-width="0.8"></rect>
              <rect x="164" y="106" width="56" height="6" rx="2" fill="#53301b"></rect>
              <rect id="loop-cursor" x="226" y="104" width="5" height="10" fill="#857243"></rect>
            </g>
            <g id="station-review">
              <rect x="296" y="330" width="120" height="64" rx="5" fill="#ffffff" stroke="#14161b" stroke-width="1.4"></rect>
              <text x="310" y="350" font-size="9" letter-spacing="1.5" fill="#14161b">02 REVIEW</text>
              <circle cx="322" cy="372" r="9" fill="none" stroke="#14161b" stroke-width="1.2"></circle>
              <circle cx="322" cy="369" r="3" fill="none" stroke="#14161b" stroke-width="1.2"></circle>
              <path d="M316 378 q6 -5 12 0" fill="none" stroke="#14161b" stroke-width="1.2"></path>
              <path id="loop-review-check" d="M342 372 l4 4 8 -9" fill="none" stroke="#53301b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
              <text x="362" y="377" font-size="8" letter-spacing="1" fill="#53301b">OK</text>
            </g>
            <g id="station-verify">
              <rect x="24" y="330" width="120" height="64" rx="5" fill="#ffffff" stroke="#14161b" stroke-width="1.4"></rect>
              <text x="38" y="350" font-size="9" letter-spacing="1.5" fill="#14161b">03 VERIFY</text>
              <g id="verify-ticks">
                <g id="vt-1" class="kv-tick-pop">
                  <rect x="38" y="360" width="13" height="13" rx="2" fill="none" stroke="rgba(83,48,27,0.28)" stroke-width="1"></rect>
                  <path d="M41 366.5 l2.5 2.5 5 -5" fill="none" stroke="#53301b" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"></path>
                </g>
                <g id="vt-2" class="kv-tick-pop">
                  <rect x="57" y="360" width="13" height="13" rx="2" fill="none" stroke="rgba(83,48,27,0.28)" stroke-width="1"></rect>
                  <path d="M60 366.5 l2.5 2.5 5 -5" fill="none" stroke="#53301b" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"></path>
                </g>
                <g id="vt-3" class="kv-tick-pop">
                  <rect x="76" y="360" width="13" height="13" rx="2" fill="none" stroke="rgba(83,48,27,0.28)" stroke-width="1"></rect>
                  <path d="M79 366.5 l2.5 2.5 5 -5" fill="none" stroke="#53301b" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"></path>
                </g>
              </g>
            </g>
            <g id="station-links" stroke="rgba(83,48,27,0.28)" stroke-width="1" stroke-dasharray="4 4" fill="none">
              <path d="M220 110 v-14"></path>
              <path d="M341 330 l-20 -26"></path>
              <path d="M99 330 l20 -26"></path>
            </g>
            <g id="dim-bottom" stroke="rgba(20,22,27,0.4)" stroke-width="1">
              <line x1="50" y1="470" x2="390" y2="470"></line>
              <line x1="50" y1="464" x2="50" y2="476"></line>
              <line x1="390" y1="464" x2="390" y2="476"></line>
            </g>
            <text x="220" y="490" text-anchor="middle" font-size="9" letter-spacing="2" fill="#6c707a">CYCLE TIME — FAST · VERIFIED</text>
            <g id="logo-dots" fill="none" stroke="#53301b" stroke-width="2.2">
              <circle cx="34" cy="40" r="5"></circle><circle cx="52" cy="40" r="5" fill="#53301b"></circle><circle cx="70" cy="40" r="5"></circle>
              <circle cx="34" cy="58" r="5"></circle><circle cx="52" cy="58" r="5"></circle><circle cx="70" cy="58" r="5" fill="#53301b"></circle>
              <circle cx="34" cy="76" r="5" fill="#53301b"></circle><circle cx="52" cy="76" r="5"></circle><circle cx="70" cy="76" r="5"></circle>
            </g>
            <g id="frame-ticks" stroke="#14161b" stroke-width="1.4">
              <path d="M6 18 v-12 h12" fill="none"></path>
              <path d="M434 522 v12 h-12" fill="none"></path>
            </g>
          </svg>
        </div>
      </div>
    </div>

    <a href="#about" class="hero__scroll" @click="scrollTo($event, 'about')" aria-label="下へスクロール">
      <span>Scroll</span>
      <span class="hero__scroll-line" aria-hidden="true"></span>
    </a>
  </section>
</template>

<script>
export default {
  name: 'HeroSection',
  methods: {
    scrollTo(event, id) {
      const target = document.getElementById(id)
      if (target) {
        event.preventDefault()
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        history.replaceState(null, '', `#${id}`)
      }
    },
  },
}
</script>

<style scoped lang="scss">
.hero {
  position: relative;
  min-height: 100vh;
  min-height: 100svh;
  display: flex;
  align-items: center;
  padding-top: calc(var(--header-h) + 40px);
  padding-bottom: 80px;
  overflow: hidden;
}

.hero__bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.hero__bg::before {
  // accent glow, kept subtle for a light theme
  content: "";
  position: absolute;
  top: -12%;
  right: -6%;
  width: 46vw;
  height: 46vw;
  max-width: 620px;
  max-height: 620px;
  background: radial-gradient(circle at center, var(--accent-mist), transparent 68%);
  filter: blur(10px);
}

.hero__ghost {
  position: absolute;
  left: -2%;
  bottom: 4%;
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(4rem, 17vw, 15rem);
  line-height: 1;
  letter-spacing: -0.02em;
  color: transparent;
  -webkit-text-stroke: 1px rgba(20, 22, 27, 0.045);
  white-space: nowrap;
  user-select: none;
}

.hero__grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  align-items: center;
  gap: 40px;
}

.hero__body {
  min-width: 0;
}

.hero__eyebrow {
  display: flex;
  max-width: 100%;
  margin-bottom: 26px;
}

.hero__title {
  font-family: var(--font-jp);
  font-weight: 900;
  font-size: clamp(1.9rem, 4.8vw, 3.6rem);
  line-height: 1.16;
  letter-spacing: 0.01em;
  color: var(--ink);
  overflow-wrap: break-word;
  text-wrap: balance;
}

.hl-accent {
  color: var(--accent);
}

.hl-underline {
  position: relative;
  white-space: nowrap;
}

.hl-underline::after {
  content: "";
  position: absolute;
  left: -2px;
  right: -2px;
  bottom: 0.06em;
  height: 0.16em;
  background: var(--accent-line);
  border-radius: 2px;
  z-index: -1;
}

.hero__lead {
  max-width: 40ch;
  margin-top: 28px;
  font-size: clamp(1rem, 1.4vw, 1.14rem);
  line-height: 1.85;
  color: var(--ink-soft);
}

.hero__cta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 38px;
}

.hero__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 44px;
  font-family: var(--font-mono);
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  color: var(--muted);

  li span {
    color: var(--ink-soft);
  }
}

.hero__meta-tag {
  margin-left: 6px;
  padding: 4px 12px;
  color: var(--accent) !important;
  border: 1px solid var(--accent-line);
  border-radius: 999px;
  font-weight: 500;
}

/* --- Key visual --- */
.hero__visual {
  display: flex;
  justify-content: center;
}

.kv-desktop {
  width: min(100%, 380px);
  aspect-ratio: 440 / 540;
}

.kv-desktop .kv-svg {
  width: 100%;
  height: 100%;
  display: block;
  overflow: visible;
}

.kv-mobile {
  display: none;
}

.kv-mobile .kv-svg {
  width: 100%;
  height: auto;
  display: block;
  overflow: visible;
}

/* Orbiting cobalt sweep (7s constant loop, pivots on the dial centre) */
#sweep {
  transform-box: view-box;
  transform-origin: 220px 250px;
  animation: kv-sweep 7s linear infinite;
}

#m-sweep {
  transform-box: view-box;
  transform-origin: 60px 60px;
  animation: kv-sweep 7s linear infinite;
}

@keyframes kv-sweep {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Station pings flash as the sweep tip passes each station (7s cycle, phased) */
.kv-ping {
  transform-box: fill-box;
  transform-origin: center;
  opacity: 0.16;
  animation: kv-ping 7s linear infinite;
}

#ping-review {
  animation-delay: 0.3s;
}

#ping-verify {
  animation-delay: 2.05s;
}

#ping-generate {
  animation-delay: 4.67s;
}

@keyframes kv-ping {
  0%,
  100% {
    opacity: 0.16;
    transform: scale(0.7);
  }
  3% {
    opacity: 1;
    transform: scale(1.35);
  }
  12% {
    opacity: 0.16;
    transform: scale(0.7);
  }
}

/* First-load choreography: cursor caret → review check draws → verify ticks pop */
#loop-cursor {
  animation: kv-caret 1.1s step-end infinite;
}

@keyframes kv-caret {
  0%,
  50% {
    opacity: 1;
  }
  50.01%,
  100% {
    opacity: 0;
  }
}

#loop-review-check {
  stroke-dasharray: 22;
  stroke-dashoffset: 22;
  animation: kv-draw 0.5s var(--ease) 1.1s forwards;
}

@keyframes kv-draw {
  to {
    stroke-dashoffset: 0;
  }
}

.kv-tick-pop {
  transform-box: fill-box;
  transform-origin: center;
  opacity: 0;
  transform: scale(0.5);
  animation: kv-tick 0.42s var(--ease) forwards;
}

#vt-1 {
  animation-delay: 1.5s;
}

#vt-2 {
  animation-delay: 1.8s;
}

#vt-3 {
  animation-delay: 2.1s;
}

@keyframes kv-tick {
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* --- Scroll cue --- */
.hero__scroll {
  position: absolute;
  left: 50%;
  bottom: 26px;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  font-family: var(--font-mono);
  font-size: 0.64rem;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--muted);
}

.hero__scroll-line {
  width: 1px;
  height: 42px;
  background: linear-gradient(var(--ink), transparent);
  animation: scrollPulse 2.2s var(--ease) infinite;
  transform-origin: top;
}

@keyframes scrollPulse {
  0%, 100% {
    transform: scaleY(0.4);
    opacity: 0.4;
  }
  50% {
    transform: scaleY(1);
    opacity: 1;
  }
}

@media (max-width: 900px) {
  .hero__grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  .hero__visual {
    display: none;
  }
  .kv-mobile {
    display: block;
    margin-top: 28px;
  }
  .hero__scroll {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero__scroll-line {
    animation: none;
  }
  /* Freeze the key visual in a complete, coherent still */
  #sweep,
  #m-sweep,
  .kv-ping,
  #loop-cursor,
  #loop-review-check,
  .kv-tick-pop {
    animation: none !important;
  }
  #loop-review-check {
    stroke-dasharray: none;
    stroke-dashoffset: 0;
  }
  .kv-tick-pop {
    opacity: 1;
    transform: none;
  }
  .kv-ping {
    opacity: 1;
  }
  #loop-cursor {
    opacity: 1;
  }
}
</style>
